import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FiSend } from "react-icons/fi";
import { Modal, Button, FormField, Banner } from "../common";
import { inviteEmployee, suggestUsername } from "../../services/invitationApi";
import { getErrorMessage, getErrorCode } from "../../utils/errors";

export default function InviteLoginModal({ employee, onClose, onSent }) {
  const [username, setUsername] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (employee) { setUsername(suggestUsername(employee)); setError(""); }
  }, [employee]);

  const send = async () => {
    if (!/^[a-z0-9_.@-]{3,100}$/.test(username)) {
      setError("3 to 100 characters: letters, digits, . _ @ -");
      return;
    }
    setBusy(true);
    try {
      const { data } = await inviteEmployee({ employeeId: employee.id, username });
      toast.success(`Invitation for ${employee.name} is queued. The email goes to ${data.email} within a minute.`);
      onSent?.(data);
      onClose();
    } catch (err) {
      const code = getErrorCode(err);
      setError(code === "ACCOUNT_EXISTS" ? `${employee.name} already has a login. Use "Resend" in Login invitations, or reset the password.`
        : code === "INVITATION_UNAVAILABLE" ? "Invitation emails are not configured on the server yet. Ask the administrator to set up SMTP and ACCOUNT_INVITATION_URL."
          : getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={Boolean(employee)}
      size="sm"
      title={`Invite ${employee?.name || ""} to the PMS`}
      subtitle="Creates their login and emails a link to choose a password. The link works once and expires after 72 hours."
      onClose={busy ? undefined : onClose}
      footer={<><Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button><Button icon={FiSend} onClick={send} loading={busy}>Send invitation</Button></>}
    >
      {employee && (
        <div className="form-stack">
          <FormField label="Username" error={error || undefined} hint="Letters, digits, . _ @ - (stored in lower case)">
            <input value={username} onChange={(e) => { setUsername(e.target.value.toLowerCase()); setError(""); }} />
          </FormField>
          <Banner tone="info" title={`Email to ${employee.email || "their official email"}`}>
            The email contains the username and the setup link only, never a password. The login starts with the Employee
            role (own records only); an administrator adds other roles under Users &amp; roles.
          </Banner>
        </div>
      )}
    </Modal>
  );
}
